import * as React from "react";
import { SmartLink } from "./link-context";

// ---------------------------------------------------------------------------
// SiteShell — top-nav + main + footer frame matching the real app layout
// ---------------------------------------------------------------------------
export interface SiteShellProps {
  children: React.ReactNode;
  className?: string;
}
export function SiteShell({ children, className = "" }: SiteShellProps) {
  return <div className={["ds-site-frame", className].filter(Boolean).join(" ")}>{children}</div>;
}

// ---------------------------------------------------------------------------
// SiteHeader — sticky top nav container
// ---------------------------------------------------------------------------
export interface SiteHeaderProps {
  brand: React.ReactNode;
  nav?: React.ReactNode;
  right?: React.ReactNode;
  ariaLabel?: string;
}
export function SiteHeader({ brand, nav, right, ariaLabel = "Primary" }: SiteHeaderProps) {
  return (
    <header role="banner" className="nav-header">
      <nav className="nav" aria-label={ariaLabel}>
        <div className="nav-inner">
          {brand}
          {nav}
          {right ? <div className="nav-right">{right}</div> : null}
        </div>
      </nav>
    </header>
  );
}

// ---------------------------------------------------------------------------
// SiteBrand — logo + wordmark, links to home
// ---------------------------------------------------------------------------
export interface SiteBrandProps {
  href?: string;
  logoSrc?: string;
  name: React.ReactNode;
}
export function SiteBrand({ href = "/", logoSrc, name }: SiteBrandProps) {
  return (
    <SmartLink href={href} className="nav-brand">
      {logoSrc ? <img src={logoSrc} alt="" aria-hidden="true" /> : null}
      <span>{name}</span>
    </SmartLink>
  );
}

// ---------------------------------------------------------------------------
// SiteNav — <ul> of nav items
// ---------------------------------------------------------------------------
export function SiteNav({ children }: { children: React.ReactNode }) {
  return <ul className="nav-links">{children}</ul>;
}

export interface SiteNavItemProps {
  href: string;
  active?: boolean;
  children: React.ReactNode;
}
export function SiteNavItem({ href, active, children }: SiteNavItemProps) {
  return (
    <li>
      <SmartLink href={href} className={active ? "active" : undefined}>
        {children}
      </SmartLink>
    </li>
  );
}

// ---------------------------------------------------------------------------
// SiteNavFlyout — dropdown menu item ("Products")
// ---------------------------------------------------------------------------
export interface SiteNavFlyoutProps {
  label: React.ReactNode;
  children: React.ReactNode;
}
export function SiteNavFlyout({ label, children }: SiteNavFlyoutProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLLIElement>(null);

  const close = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, close]);

  return (
    <li className="nav-flyout" ref={ref}>
      <span
        className="nav-flyout-trigger"
        tabIndex={0}
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        {label}
      </span>
      {open ? (
        <div className="nav-flyout-menu" role="menu" onClick={close}>
          {children}
        </div>
      ) : null}
    </li>
  );
}

export interface SiteNavFlyoutItemProps {
  href: string;
  label: React.ReactNode;
  description?: React.ReactNode;
}
export function SiteNavFlyoutItem({ href, label, description }: SiteNavFlyoutItemProps) {
  return (
    <SmartLink href={href} role="menuitem">
      <span className="nav-flyout-item-label">{label}</span>
      {description ? <span className="nav-flyout-item-desc">{description}</span> : null}
    </SmartLink>
  );
}

// ---------------------------------------------------------------------------
// SiteMain — main content region with the route animation stage
// ---------------------------------------------------------------------------
export function SiteMain({ children }: { children: React.ReactNode }) {
  return <main id="main-content">{children}</main>;
}

// ---------------------------------------------------------------------------
// SiteFooter — footer with responsive column grid
// ---------------------------------------------------------------------------
export function SiteFooter({ children }: { children: React.ReactNode }) {
  return (
    <footer className="footer" aria-label="Site footer">
      <div className="container">{children}</div>
    </footer>
  );
}

export function SiteFooterGrid({ children }: { children: React.ReactNode }) {
  return <div className="footer-grid">{children}</div>;
}

export interface SiteFooterColumnProps {
  heading?: React.ReactNode;
  children: React.ReactNode;
}
export function SiteFooterColumn({ heading, children }: SiteFooterColumnProps) {
  return (
    <div>
      {heading ? <h4>{heading}</h4> : null}
      {children}
    </div>
  );
}

export interface SiteFooterBottomProps {
  children: React.ReactNode;
}
export function SiteFooterBottom({ children }: SiteFooterBottomProps) {
  return <div className="footer-bottom">{children}</div>;
}
